<template>
  <div class="max-w-4xl mx-auto space-y-6 pb-12">
    
    <!-- ═════════════════════ TOP PROFILE HERO & TABS ═════════════════════ -->
    <div class="bg-background-light rounded-2xl shadow-sm border border-background-lighter p-6 sm:p-7 relative overflow-hidden">
      <!-- Decorative background glow -->
      <div class="absolute -right-12 -top-12 w-56 h-56 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>

      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-5 relative z-10">
        <!-- User Avatar & Identity -->
        <div class="flex items-center gap-4 sm:gap-5">
          <div class="relative shrink-0">
            <div class="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary to-blue-600 rounded-2xl flex items-center justify-center text-white text-2xl font-black shadow-lg shadow-primary/20">
              {{ userInitials }}
            </div>
            <span v-if="authStore.user?.is_active" class="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 border-2 border-background-light rounded-full flex items-center justify-center text-white text-[10px] font-bold" title="Active Account">
              ✓
            </span>
          </div>

          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <h1 class="text-xl sm:text-2xl font-black text-text-primary tracking-tight truncate">
                {{ profileForm.name || authStore.user?.name || 'My Account' }}
              </h1>
              <span v-if="authStore.user?.is_active" class="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                Active
              </span>
            </div>
            <p class="text-xs sm:text-sm text-text-secondary mt-0.5 truncate">{{ authStore.user?.email }}</p>
            
            <div class="flex items-center gap-2 mt-2 flex-wrap">
              <span v-if="authStore.user?.is_active" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-background border border-background-lighter text-[11px] text-text-secondary font-medium">
                <span>💳 Wallet:</span>
                <strong class="text-primary font-bold">{{ getActiveWalletName() }}</strong>
              </span>
              <span v-if="globalRatingForm.id" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-500 border border-amber-500/20 text-[11px] font-bold">
                ⭐ {{ globalRatingForm.rating }}/5 given
              </span>
            </div>
          </div>
        </div>

        <!-- Quick Balance Summary -->
        <div class="flex sm:flex-col items-center sm:items-end justify-between border-t sm:border-t-0 pt-3 sm:pt-0 border-background-lighter shrink-0">
          <span class="text-[11px] text-text-secondary uppercase tracking-wider font-semibold">Main Balance</span>
          <span class="text-lg sm:text-2xl font-black text-primary">
            {{ formatCurrency(getActiveWalletBalance(), getActiveWalletCurrency()) }}
          </span>
        </div>
      </div>

      <!-- Segmented Navigation Tabs -->
      <div class="mt-6 pt-5 border-t border-background-lighter">
        <div
          :class="[
            'grid gap-2 sm:flex sm:items-center sm:gap-2',
            tabs.length === 3 ? 'grid-cols-3' : 'grid-cols-2'
          ]"
        >
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            @click="activeTab = tab.id"
            :class="[
              'flex items-center justify-center sm:justify-start gap-2 px-3 py-2.5 sm:px-4 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all duration-200 select-none border',
              activeTab === tab.id
                ? 'bg-primary text-white border-primary shadow-md shadow-primary/25'
                : 'bg-background/70 sm:bg-transparent border-background-lighter sm:border-transparent text-text-secondary hover:text-text-primary hover:bg-background-lighter'
            ]"
          >
            <span class="text-sm shrink-0">{{ tab.icon }}</span>
            <span class="truncate">
              <span class="sm:hidden">{{ tab.shortLabel || tab.label }}</span>
              <span class="hidden sm:inline">{{ tab.label }}</span>
            </span>
            <span
              v-if="tab.badge"
              :class="[
                'px-1.5 py-0.5 rounded-full text-[10px] font-extrabold shrink-0',
                activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary border border-primary/20'
              ]"
            >
              {{ tab.badge }}
            </span>
          </button>
        </div>
      </div>
    </div>


    <!-- ═════════════════════ TAB 1: PROFILE & SECURITY ═════════════════════ -->
    <div v-show="activeTab === 'profile'" class="space-y-6 animate-fade-in">
      <!-- Profile Information Card -->
      <div class="bg-background-light rounded-2xl shadow-sm border border-background-lighter p-6 sm:p-7">
        <div class="pb-5 border-b border-background-lighter flex items-center justify-between">
          <div>
            <h2 class="text-lg sm:text-xl font-bold text-text-primary flex items-center gap-2">
              <span>👤</span> Personal Information
            </h2>
            <p class="text-xs sm:text-sm text-text-secondary mt-0.5">Manage your name, phone number, and personal bio</p>
          </div>
          <span v-if="hasProfileChanges" class="text-xs text-amber-500 font-bold bg-amber-500/10 px-2.5 py-1 rounded-full border border-amber-500/20">
            Unsaved changes
          </span>
        </div>

        <form @submit.prevent="updateProfile" class="mt-6 space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Full Name -->
            <div>
              <label for="name" class="block text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">
                Full Name <span class="text-red-500">*</span>
              </label>
              <input
                id="name"
                v-model="profileForm.name"
                type="text"
                :disabled="isUpdatingProfile"
                class="w-full px-4 py-3 border border-background-lighter rounded-xl bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60 transition-all"
                placeholder="Your full name"
                required
              />
              <p v-if="errors.name" class="mt-1 text-xs text-red-500">{{ errors.name[0] }}</p>
            </div>

            <!-- Phone -->
            <div>
              <label for="phone" class="block text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">
                Phone Number
              </label>
              <input
                id="phone"
                v-model="profileForm.phone"
                type="tel"
                :disabled="isUpdatingProfile"
                class="w-full px-4 py-3 border border-background-lighter rounded-xl bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60 transition-all"
                placeholder="e.g. 0550 12 34 56"
              />
              <p v-if="errors.phone" class="mt-1 text-xs text-red-500">{{ errors.phone[0] }}</p>
            </div>
          </div>

          <!-- Description / Bio -->
          <div>
            <label for="description" class="block text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">
              Bio / Personal Note (optional)
            </label>
            <textarea
              id="description"
              v-model="profileForm.description"
              rows="3"
              :disabled="isUpdatingProfile"
              class="w-full px-4 py-3 border border-background-lighter rounded-xl bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60 resize-none transition-all"
              placeholder="Add a short description or note..."
            ></textarea>
            <p v-if="errors.description" class="mt-1 text-xs text-red-500">{{ errors.description[0] }}</p>
          </div>

          <div class="flex justify-end pt-2">
            <button
              type="submit"
              :disabled="isUpdatingProfile || !hasProfileChanges"
              class="inline-flex items-center px-6 py-3 text-sm font-bold rounded-xl text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md shadow-primary/20"
            >
              <div v-if="isUpdatingProfile" class="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              {{ isUpdatingProfile ? 'Updating...' : 'Save Profile' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Change Password Card -->
      <div class="bg-background-light rounded-2xl shadow-sm border border-background-lighter p-6 sm:p-7">
        <div class="pb-5 border-b border-background-lighter">
          <h2 class="text-lg sm:text-xl font-bold text-text-primary flex items-center gap-2">
            <span>🔒</span> Account Security & Password
          </h2>
          <p class="text-xs sm:text-sm text-text-secondary mt-0.5">Change your password to keep your account safe</p>
        </div>

        <form @submit.prevent="updatePassword" class="mt-6 space-y-5">
          <!-- Current Password -->
          <div>
            <label for="current_password" class="block text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">
              Current Password <span class="text-red-500">*</span>
            </label>
            <input
              id="current_password"
              v-model="passwordForm.current_password"
              type="password"
              :disabled="isUpdatingPassword"
              class="w-full px-4 py-3 border border-background-lighter rounded-xl bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60 transition-all"
              placeholder="Enter your current password"
              required
            />
            <p v-if="errors.current_password" class="mt-1 text-xs text-red-500">{{ errors.current_password[0] }}</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- New Password -->
            <div>
              <label for="new_password" class="block text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">
                New Password <span class="text-red-500">*</span>
              </label>
              <input
                id="new_password"
                v-model="passwordForm.new_password"
                type="password"
                :disabled="isUpdatingPassword"
                class="w-full px-4 py-3 border border-background-lighter rounded-xl bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60 transition-all"
                placeholder="At least 8 characters"
                required
                minlength="8"
              />
              <p v-if="errors.new_password" class="mt-1 text-xs text-red-500">{{ errors.new_password[0] }}</p>
            </div>

            <!-- Confirm New Password -->
            <div>
              <label for="new_password_confirmation" class="block text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">
                Confirm New Password <span class="text-red-500">*</span>
              </label>
              <input
                id="new_password_confirmation"
                v-model="passwordForm.new_password_confirmation"
                type="password"
                :disabled="isUpdatingPassword"
                class="w-full px-4 py-3 border border-background-lighter rounded-xl bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60 transition-all"
                placeholder="Re-enter to confirm"
                required
                minlength="8"
              />
              <p v-if="errors.new_password_confirmation" class="mt-1 text-xs text-red-500">{{ errors.new_password_confirmation[0] }}</p>
            </div>
          </div>

          <div class="flex justify-end pt-2">
            <button
              type="submit"
              :disabled="isUpdatingPassword || !hasPasswordChanges"
              class="inline-flex items-center px-6 py-3 text-sm font-bold rounded-xl text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md shadow-primary/20"
            >
              <div v-if="isUpdatingPassword" class="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              {{ isUpdatingPassword ? 'Updating...' : 'Change Password' }}
            </button>
          </div>
        </form>
      </div>
    </div>


    <!-- ═════════════════════ TAB 2: WALLETS & CURRENCIES ═════════════════════ -->
    <div v-show="activeTab === 'wallet'" class="space-y-6 animate-fade-in">
      <div v-if="`${authStore.user.is_active}` === '1' && !dataStore.shouldHideWallet" class="bg-background-light rounded-2xl shadow-sm border border-background-lighter p-6 sm:p-7">
        <div class="pb-5 border-b border-background-lighter flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 class="text-lg sm:text-xl font-bold text-text-primary flex items-center gap-2">
              <span>💳</span> Select Active Wallet
            </h2>
            <p class="text-xs sm:text-sm text-text-secondary mt-0.5">Choose your default currency for orders, prices, and checkout</p>
          </div>
          <div v-if="isUpdatingWallet" class="flex items-center text-xs text-primary font-bold animate-pulse">
            <div class="animate-spin -ml-1 mr-1.5 h-3.5 w-3.5 border-2 border-primary border-t-transparent rounded-full"></div>
            Updating active wallet...
          </div>
        </div>

        <!-- Sleek Responsive Cards Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <!-- USD -->
          <div
            @click="selectWallet('0')"
            :class="[
              'relative p-5 rounded-2xl border cursor-pointer transition-all duration-200 select-none flex flex-col justify-between h-36',
              selectedWallet === '0'
                ? 'bg-primary/5 border-primary shadow-md ring-2 ring-primary/20'
                : 'bg-background border-background-lighter hover:border-primary/40 hover:bg-background-lighter/40'
            ]"
          >
            <div class="flex items-center justify-between">
              <span class="text-2xl">🇺🇸</span>
              <span v-if="selectedWallet === '0'" class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-primary text-white">
                Active ✓
              </span>
            </div>
            <div>
              <p class="text-xs text-text-secondary font-semibold">US Dollar (USD)</p>
              <p class="text-lg font-black text-text-primary mt-0.5">
                {{ formatCurrency(walletBalances.usd || 0, 'USD') }}
              </p>
            </div>
          </div>

          <!-- EUR -->
          <div
            @click="selectWallet('1')"
            :class="[
              'relative p-5 rounded-2xl border cursor-pointer transition-all duration-200 select-none flex flex-col justify-between h-36',
              selectedWallet === '1'
                ? 'bg-primary/5 border-primary shadow-md ring-2 ring-primary/20'
                : 'bg-background border-background-lighter hover:border-primary/40 hover:bg-background-lighter/40'
            ]"
          >
            <div class="flex items-center justify-between">
              <span class="text-2xl">🇪🇺</span>
              <span v-if="selectedWallet === '1'" class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-primary text-white">
                Active ✓
              </span>
            </div>
            <div>
              <p class="text-xs text-text-secondary font-semibold">Euro (EUR)</p>
              <p class="text-lg font-black text-text-primary mt-0.5">
                {{ formatCurrency(walletBalances.eur || 0, 'EUR') }}
              </p>
            </div>
          </div>

          <!-- DZD -->
          <div
            @click="selectWallet('2')"
            :class="[
              'relative p-5 rounded-2xl border cursor-pointer transition-all duration-200 select-none flex flex-col justify-between h-36',
              selectedWallet === '2'
                ? 'bg-primary/5 border-primary shadow-md ring-2 ring-primary/20'
                : 'bg-background border-background-lighter hover:border-primary/40 hover:bg-background-lighter/40'
            ]"
          >
            <div class="flex items-center justify-between">
              <span class="text-2xl">🇩🇿</span>
              <span v-if="selectedWallet === '2'" class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-primary text-white">
                Active ✓
              </span>
            </div>
            <div>
              <p class="text-xs text-text-secondary font-semibold">Algerian Dinar (DZD)</p>
              <p class="text-lg font-black text-text-primary mt-0.5">
                {{ formatCurrency(walletBalances.dzd || 0, 'DZD') }}
              </p>
            </div>
          </div>

          <!-- EGP -->
          <div
            @click="selectWallet('3')"
            :class="[
              'relative p-5 rounded-2xl border cursor-pointer transition-all duration-200 select-none flex flex-col justify-between h-36',
              selectedWallet === '3'
                ? 'bg-primary/5 border-primary shadow-md ring-2 ring-primary/20'
                : 'bg-background border-background-lighter hover:border-primary/40 hover:bg-background-lighter/40'
            ]"
          >
            <div class="flex items-center justify-between">
              <span class="text-2xl">🇪🇬</span>
              <span v-if="selectedWallet === '3'" class="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-primary text-white">
                Active ✓
              </span>
            </div>
            <div>
              <p class="text-xs text-text-secondary font-semibold">Egyptian Pound (EGP)</p>
              <p class="text-lg font-black text-text-primary mt-0.5">
                {{ formatCurrency(walletBalances.egp || 0, 'EGP') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Flexy Wallet (if enabled) -->
        <div v-if="authStore.user.is_flexy === '1'" class="mt-4 p-4 rounded-2xl border border-background-lighter bg-background flex items-center justify-between">
          <div class="flex items-center gap-3">
            <span class="text-2xl">⚡</span>
            <div>
              <p class="text-xs font-bold text-text-primary">Flexy Wallet (Read-only)</p>
              <p class="text-xs text-text-secondary">Dedicated to automated mobile phone top-ups</p>
            </div>
          </div>
          <span class="px-3 py-1 rounded-full text-xs font-extrabold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
            {{ formatCurrency(walletBalances.flexy || 0, 'DZD') }}
          </span>
        </div>
      </div>
    </div>


    <!-- ═════════════════════ TAB 3: MY REVIEW ON ESKRAG ═════════════════════ -->
    <div v-show="activeTab === 'rating'" class="space-y-6 animate-fade-in">
      <div class="bg-background-light rounded-2xl shadow-sm border border-amber-500/25 p-6 sm:p-7 relative overflow-hidden">
        <div class="absolute -right-8 -top-8 w-40 h-40 bg-amber-500/10 rounded-full blur-2xl pointer-events-none"></div>

        <div class="pb-5 border-b border-background-lighter flex items-center justify-between flex-wrap gap-2 relative z-10">
          <div>
            <h2 class="text-lg sm:text-xl font-bold text-text-primary flex items-center gap-2">
              <span class="text-amber-400">⭐</span> Your Overall Review of Eskrag
            </h2>
            <p class="text-xs sm:text-sm text-text-secondary mt-0.5">Share your feedback to help the entire community</p>
          </div>
          <span v-if="globalRatingForm.id" class="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/15 text-amber-500 border border-amber-500/30">
            Review Submitted
          </span>
        </div>

        <!-- Case 1: User has no completed order -->
        <div v-if="!canRateGlobal && !isLoadingGlobalRating" class="mt-6 p-6 rounded-2xl bg-background border border-background-lighter flex items-start gap-4">
          <span class="text-3xl flex-shrink-0">🛍️</span>
          <div>
            <h3 class="font-bold text-sm text-text-primary mb-1">Reviews reserved for verified customers</h3>
            <p class="text-xs text-text-secondary leading-relaxed">
              To ensure verified and authentic reviews, you must complete at least one order on Eskrag before sharing your overall rating.
            </p>
          </div>
        </div>

        <!-- Case 2: Loading state -->
        <div v-else-if="isLoadingGlobalRating" class="py-12 flex flex-col items-center justify-center text-text-secondary text-sm">
          <div class="animate-spin h-6 w-6 border-2 border-amber-500 border-t-transparent rounded-full mb-2"></div>
          <span>Loading your review...</span>
        </div>

        <!-- Case 3: Can rate -->
        <form v-else @submit.prevent="submitGlobalRating" class="mt-6 space-y-6 relative z-10">
          <!-- Interactive Stars -->
          <div class="p-4 rounded-2xl bg-background border border-background-lighter">
            <label class="block text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">
              Overall Rating <span class="text-red-500">*</span>
            </label>
            <div class="flex items-center gap-3">
              <div class="flex gap-1.5 text-3xl cursor-pointer select-none">
                <span
                  v-for="star in 5"
                  :key="star"
                  @click="globalRatingForm.rating = star"
                  @mouseenter="hoverStar = star"
                  @mouseleave="hoverStar = 0"
                  class="transition-transform duration-150 hover:scale-125"
                  :class="(hoverStar ? star <= hoverStar : star <= globalRatingForm.rating) ? 'text-amber-400 drop-shadow-sm' : 'text-gray-300 dark:text-gray-700'"
                >
                  ★
                </span>
              </div>
              <span class="text-sm font-bold text-amber-400">
                {{ starLabels[hoverStar || globalRatingForm.rating] || 'Select a rating' }}
              </span>
            </div>
          </div>

          <!-- Quick tag suggestions -->
          <div>
            <label class="block text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">
              Quick feedback tags (click to add):
            </label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="tag in ratingTagSuggestions"
                :key="tag"
                type="button"
                @click="addTagToComment(tag)"
                class="px-3 py-1.5 rounded-lg text-xs font-semibold bg-background border border-background-lighter hover:border-amber-400 hover:text-amber-500 transition-all text-text-secondary"
              >
                + {{ tag }}
              </button>
            </div>
          </div>

          <!-- Comment Textarea -->
          <div>
            <label for="global_comment" class="block text-xs font-bold uppercase tracking-wider text-text-secondary mb-2">
              Your Review (optional)
            </label>
            <textarea
              id="global_comment"
              v-model="globalRatingForm.comment"
              rows="3"
              maxlength="1000"
              class="w-full px-4 py-3 border border-background-lighter rounded-xl bg-background text-sm font-medium focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 resize-none transition-all"
              placeholder="What do you think of Eskrag? (Delivery speed, customer support, catalog, overall experience...)"
            ></textarea>
            <div class="flex justify-between text-xs text-text-secondary mt-1">
              <span>Your feedback is valuable to us!</span>
              <span>{{ (globalRatingForm.comment || '').length }}/1000</span>
            </div>
          </div>

          <!-- Anonymous Option -->
          <div class="flex items-center">
            <input
              id="global_is_anonymous"
              type="checkbox"
              v-model="globalRatingForm.is_anonymous"
              class="h-4 w-4 text-primary focus:ring-primary border-background-lighter rounded bg-background"
            />
            <label for="global_is_anonymous" class="ml-2.5 block text-xs text-text-secondary cursor-pointer select-none">
              Post anonymously (your name will not be shown publicly)
            </label>
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end pt-2">
            <button
              type="submit"
              :disabled="isSubmittingGlobalRating || !globalRatingForm.rating"
              class="inline-flex items-center px-6 py-3 text-sm font-bold rounded-xl text-white bg-amber-500 hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md shadow-amber-500/20"
            >
              <div v-if="isSubmittingGlobalRating" class="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              {{ globalRatingForm.id ? 'Update My Review' : 'Submit My Review' }}
            </button>
          </div>
        </form>
      </div>
    </div>


    <!-- ═════════════════════ TAB 4: NOTIFICATIONS ═════════════════════ -->
    <div v-show="activeTab === 'notifications'" class="space-y-6 animate-fade-in">
      <div class="bg-background-light rounded-2xl shadow-sm border border-background-lighter p-6 sm:p-7">
        <div class="pb-5 border-b border-background-lighter">
          <h2 class="text-lg sm:text-xl font-bold text-text-primary flex items-center gap-2">
            <span>🔔</span> Notification Preferences
          </h2>
          <p class="text-xs sm:text-sm text-text-secondary mt-0.5">Choose how you want to be notified about orders and support messages</p>
        </div>

        <div class="mt-6 space-y-4">
          <!-- Email Notifications Toggle -->
          <div class="flex items-center justify-between p-4 sm:p-5 border border-background-lighter rounded-2xl bg-background transition-colors">
            <div class="flex items-start gap-4">
              <div class="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p class="text-sm font-bold text-text-primary">Email Notifications</p>
                <p class="text-xs text-text-secondary mt-0.5">Receive receipts, order status updates, and support responses by email</p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              :aria-checked="notificationsEmail"
              @click="toggleEmailNotifications"
              :disabled="isUpdatingNotif"
              :class="[
                'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                notificationsEmail ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600',
                isUpdatingNotif ? 'opacity-70 cursor-wait' : 'cursor-pointer'
              ]"
            >
              <span
                :class="[
                  'pointer-events-none inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  notificationsEmail ? 'translate-x-5' : 'translate-x-0'
                ]"
              >
                <svg v-if="isUpdatingNotif" class="w-3 h-3 text-text-secondary animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </span>
            </button>
          </div>

          <!-- Push Notifications (Mobile / Desktop) -->
          <div v-if="isMobile" class="p-4 sm:p-5 border border-background-lighter rounded-2xl bg-background">
            <div class="flex items-start justify-between gap-4">
              <div class="flex items-start gap-4">
                <div class="p-2.5 rounded-xl bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p class="text-sm font-bold text-text-primary">Mobile Push Notifications</p>
                  <p class="text-xs text-text-secondary mt-0.5">Receive instant alerts even when the browser is closed</p>
                  <div class="mt-2" v-if="pushSub.supported">
                    <span v-if="pushSub.subscribed.value" class="inline-flex items-center gap-1 text-xs text-emerald-600 font-bold">
                      ✓ Push notifications active
                    </span>
                    <span v-else-if="browserNotif.isSupported() && browserNotif.permission() === 'denied'" class="text-xs text-red-500 font-bold">
                      ⚠️ Notifications blocked by browser
                    </span>
                  </div>
                </div>
              </div>
              <button
                v-if="pushSub.supported"
                @click="togglePushSub"
                :disabled="pushSub.loading.value"
                :class="[
                  'px-4 py-2 text-xs font-bold rounded-xl transition-all shadow-sm',
                  pushSub.subscribed.value
                    ? 'bg-red-50 text-red-600 border border-red-200'
                    : 'bg-primary text-white hover:bg-primary-dark shadow-primary/20'
                ]"
              >
                {{ pushSub.subscribed.value ? 'Disable' : 'Enable' }}
              </button>
            </div>
          </div>

          <!-- Desktop Notifications -->
          <div v-else class="flex items-center justify-between p-4 sm:p-5 border border-background-lighter rounded-2xl bg-background transition-colors">
            <div class="flex items-start gap-4">
              <div class="p-2.5 rounded-xl bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <div>
                <p class="text-sm font-bold text-text-primary">Live Desktop Notifications</p>
                <p class="text-xs text-text-secondary mt-0.5">Receive real-time alerts directly in your browser</p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              :aria-checked="notificationsRealtime"
              @click="toggleRealtimeNotifications"
              :disabled="isUpdatingNotif"
              :class="[
                'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                notificationsRealtime ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600',
                isUpdatingNotif ? 'opacity-70 cursor-wait' : 'cursor-pointer'
              ]"
            >
              <span
                :class="[
                  'pointer-events-none inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  notificationsRealtime ? 'translate-x-5' : 'translate-x-0'
                ]"
              >
                <svg v-if="isUpdatingNotif" class="w-3 h-3 text-text-secondary animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth.js';
import { useDataStore } from '@/stores/dataStore.js';

import api from '../../services/api';
import { updateUserPreferences } from '../../services/api';
import { useBrowserNotifications } from '@/composables/useBrowserNotifications';
import { usePushSubscription } from '@/composables/usePushSubscription';
import Swal from 'sweetalert2';

export default {
  name: 'Profile',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();
    const dataStore = useDataStore();
    const browserNotif = useBrowserNotifications();
    const pushSub = usePushSubscription();

    // Active Tab Navigation
    const activeTab = ref(route.query.tab || 'profile');

    const profileForm = ref({
      name: '',
      phone: '',
      description: ''
    });

    const passwordForm = ref({
      current_password: '',
      new_password: '',
      new_password_confirmation: ''
    });

    // Wallet related states
    const selectedWallet = ref('0'); // Default to USD (is_da = 0)
    const isUpdatingWallet = ref(false);
    const walletBalances = ref({
      usd: 0,
      eur: 0,
      dzd: 0,
      egp: 0,
      flexy: 0
    });

    // Loading states
    const isUpdatingProfile = ref(false);
    const isUpdatingPassword = ref(false);

    // Form errors
    const errors = ref({});

    // Notification states
    const notificationsEmail = ref(true);
    const notificationsRealtime = ref(true);
    const isUpdatingNotif = ref(false);
    const isMobile = ref(false);
    const showNotifHelp = ref(false);

    const checkMobile = () => {
      isMobile.value = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    // Store original profile data to detect changes
    const originalProfileData = ref({});

    // User initials for avatar
    const userInitials = computed(() => {
      const name = profileForm.value.name || authStore.user?.name || 'U';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    });

    // Check if profile form has changes
    const hasProfileChanges = computed(() => {
      return profileForm.value.name !== originalProfileData.value.name ||
          profileForm.value.phone !== originalProfileData.value.phone ||
          profileForm.value.description !== originalProfileData.value.description;
    });

    // Check if password form has changes
    const hasPasswordChanges = computed(() => {
      return passwordForm.value.current_password !== '' ||
          passwordForm.value.new_password !== '' ||
          passwordForm.value.new_password_confirmation !== '';
    });

    // Currency formatting helper
    const formatCurrency = (amount, currency) => {
      const num = Number(amount) || 0;
      switch (currency) {
        case 'USD':
          return `$${num.toFixed(2)}`;
        case 'EUR':
          return `€${num.toFixed(2)}`;
        case 'DZD':
          return `${num.toLocaleString()} DZD`;
        case 'EGP':
          return `${num.toFixed(2)} EGP`;
        default:
          return `${num.toFixed(2)} ${currency}`;
      }
    };

    const getActiveWalletName = () => {
      const walletNames = {
        '0': 'US Dollar (USD)',
        '1': 'Euro (EUR)',
        '2': 'Algerian Dinar (DZD)',
        '3': 'Egyptian Pound (EGP)'
      };
      return walletNames[selectedWallet.value] || 'Not defined';
    };

    const getActiveWalletBalance = () => {
      if (selectedWallet.value === '1') return walletBalances.value.eur || 0;
      if (selectedWallet.value === '2') return walletBalances.value.dzd || 0;
      if (selectedWallet.value === '3') return walletBalances.value.egp || 0;
      return walletBalances.value.usd || 0;
    };

    const getActiveWalletCurrency = () => {
      if (selectedWallet.value === '1') return 'EUR';
      if (selectedWallet.value === '2') return 'DZD';
      if (selectedWallet.value === '3') return 'EGP';
      return 'USD';
    };

    const selectWallet = (val) => {
      if (isUpdatingWallet.value || selectedWallet.value === val) return;
      selectedWallet.value = val;
      updateActiveWallet();
    };

    const loadUserData = () => {
      if (authStore.user) {
        const userData = {
          name: authStore.user.name || '',
          phone: authStore.user.phone || '',
          description: authStore.user.description || ''
        };

        profileForm.value = { ...userData };
        originalProfileData.value = { ...userData };

        // Load notification preferences
        notificationsEmail.value = authStore.user.notifications_email !== false;
        notificationsRealtime.value = authStore.user.notifications_realtime !== false;

        // Load wallet data
        selectedWallet.value = String(authStore.user.is_da || 0);
        walletBalances.value = {
          usd: authStore.user.wallet || 0,
          eur: authStore.user.wallet_euro || 0,
          dzd: authStore.user.wallet_da || 0,
          egp: authStore.user.wallet_egp || 0,
          flexy: authStore.user.wallet_flexy || 0
        };
      }
    };

    const clearErrors = () => {
      errors.value = {};
    };

    const updateActiveWallet = async () => {
      if (isUpdatingWallet.value) return;
      isUpdatingWallet.value = true;

      try {
        const response = await api.put('/user/wallet', {
          is_da: parseInt(selectedWallet.value)
        });

        if (response.data && response.data.success) {
          const updatedUser = {
            ...authStore.user,
            is_da: parseInt(selectedWallet.value)
          };
          authStore.user = updatedUser;
          dataStore.updateUserCurrency(parseInt(selectedWallet.value));
          localStorage.setItem('user', JSON.stringify(updatedUser));

          Swal.fire({
            title: 'Success!',
            text: `Active wallet changed to ${getActiveWalletName()}.`,
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
          });
        }
      } catch (error) {
        console.error('Wallet update error:', error);
        selectedWallet.value = String(authStore.user.is_da || 0);

        Swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to update active wallet.',
          icon: 'error',
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      } finally {
        isUpdatingWallet.value = false;
      }
    };

    const updateProfile = async () => {
      if (isUpdatingProfile.value) return;
      clearErrors();
      isUpdatingProfile.value = true;

      try {
        const response = await api.put('/user/profile', {
          name: profileForm.value.name,
          phone: profileForm.value.phone,
          description: profileForm.value.description
        });

        if (response.data && response.data.user) {
          authStore.user = { ...authStore.user, ...response.data.user };
          localStorage.setItem('user', JSON.stringify(authStore.user));
          dataStore.setUserData(authStore.user);
          originalProfileData.value = { ...profileForm.value };

          Swal.fire({
            title: 'Success!',
            text: 'Your personal information has been updated.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
          });
        }
      } catch (error) {
        console.error('Profile update error:', error);
        if (error.response?.data?.errors) {
          errors.value = error.response.data.errors;
        } else {
          Swal.fire({
            title: 'Error!',
            text: error.response?.data?.message || 'Failed to update profile.',
            icon: 'error'
          });
        }
      } finally {
        isUpdatingProfile.value = false;
      }
    };

    const updatePassword = async () => {
      if (isUpdatingPassword.value) return;
      clearErrors();

      if (passwordForm.value.new_password !== passwordForm.value.new_password_confirmation) {
        errors.value = { new_password_confirmation: ['Passwords do not match.'] };
        return;
      }

      isUpdatingPassword.value = true;

      try {
        const response = await api.post('/change-password', {
          current_password: passwordForm.value.current_password,
          password: passwordForm.value.new_password,
          password_confirmation: passwordForm.value.new_password_confirmation
        });

        if (response.data && response.data.status) {
          passwordForm.value = {
            current_password: '',
            new_password: '',
            new_password_confirmation: ''
          };

          Swal.fire({
            title: 'Success!',
            text: 'Password updated successfully.',
            icon: 'success',
            timer: 2500,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
          });
        }
      } catch (error) {
        console.error('Password update error:', error);
        if (error.response?.data?.errors) {
          errors.value = error.response.data.errors;
        } else {
          Swal.fire({
            title: 'Error!',
            text: error.response?.data?.message || 'Failed to update password.',
            icon: 'error'
          });
        }
      } finally {
        isUpdatingPassword.value = false;
      }
    };

    const toggleEmailNotifications = async () => {
      if (isUpdatingNotif.value || !authStore.user) return;
      const previous = notificationsEmail.value;
      notificationsEmail.value = !previous;
      isUpdatingNotif.value = true;

      try {
        const response = await updateUserPreferences({
          notifications_email: notificationsEmail.value ? 1 : 0
        });
        if (response && response.user) {
          authStore.user = { ...authStore.user, notifications_email: response.user.notifications_email };
          localStorage.setItem('user', JSON.stringify(authStore.user));
          dataStore.setUserData(authStore.user);
        }
      } catch (error) {
        notificationsEmail.value = previous;
        console.error('Failed to update email notification preference:', error);
        Swal.fire({ title: 'Error!', text: 'Failed to update notification preference.', icon: 'error', timer: 2000, toast: true, position: 'top-end' });
      } finally {
        isUpdatingNotif.value = false;
      }
    };

    const toggleRealtimeNotifications = async () => {
      if (isUpdatingNotif.value || !authStore.user) return;
      const previous = notificationsRealtime.value;
      notificationsRealtime.value = !previous;
      isUpdatingNotif.value = true;

      try {
        const response = await updateUserPreferences({
          notifications_realtime: notificationsRealtime.value ? 1 : 0
        });
        if (response && response.user) {
          authStore.user = { ...authStore.user, notifications_realtime: response.user.notifications_realtime };
          localStorage.setItem('user', JSON.stringify(authStore.user));
          dataStore.setUserData(authStore.user);
        }
      } catch (error) {
        notificationsRealtime.value = previous;
        console.error('Failed to update realtime notification preference:', error);
        Swal.fire({ title: 'Error!', text: 'Failed to update notification preference.', icon: 'error', timer: 2000, toast: true, position: 'top-end' });
      } finally {
        isUpdatingNotif.value = false;
      }
    };

    const togglePushSub = async () => {
      if (pushSub.subscribed.value) {
        await pushSub.unsubscribeFromPush();
        if (!pushSub.subscribed.value && notificationsRealtime.value) {
          await toggleRealtimeNotifications();
        }
      } else {
        await pushSub.subscribeToPush();
        if (pushSub.subscribed.value && !notificationsRealtime.value) {
          await toggleRealtimeNotifications();
        }
      }
    };

    const requestNotifPermission = async () => {
      await browserNotif.requestPermission();
      if (browserNotif.permission() === 'granted') {
        showNotifHelp.value = false;
      }
    };

    // ── Global Rating States & Logic ──
    const canRateGlobal = ref(false);
    const isLoadingGlobalRating = ref(true);
    const isSubmittingGlobalRating = ref(false);
    const hoverStar = ref(0);
    const starLabels = {
      1: 'Disappointing 😞',
      2: 'Fair 😐',
      3: 'Good 🙂',
      4: 'Very Good 😊',
      5: 'Excellent! 🌟'
    };
    const globalRatingForm = ref({
      id: null,
      rating: 5,
      comment: '',
      is_anonymous: false
    });

    const ratingTagSuggestions = [
      '⚡ Ultra fast delivery',
      '💬 Responsive support',
      '🎮 Best prices',
      '⭐ 100% Reliable & Clean'
    ];

    const addTagToComment = (tag) => {
      if (!globalRatingForm.value.comment) {
        globalRatingForm.value.comment = tag;
      } else if (!globalRatingForm.value.comment.includes(tag)) {
        globalRatingForm.value.comment += ' • ' + tag;
      }
    };

    const fetchGlobalRatingStatus = async () => {
      isLoadingGlobalRating.value = true;
      try {
        const response = await api.get('/ratings/user/global');
        if (response.data && response.data.success) {
          canRateGlobal.value = !!response.data.can_rate;
          if (response.data.rating) {
            globalRatingForm.value = {
              id: response.data.rating.id,
              rating: response.data.rating.rating || 5,
              comment: response.data.rating.comment || '',
              is_anonymous: !!response.data.rating.is_anonymous
            };
          }
        }
      } catch (err) {
        console.error('Error fetching global rating status:', err);
      } finally {
        isLoadingGlobalRating.value = false;
      }
    };

    const submitGlobalRating = async () => {
      if (isSubmittingGlobalRating.value || !globalRatingForm.value.rating) return;
      isSubmittingGlobalRating.value = true;
      try {
        const response = await api.post('/ratings/global', {
          rating: globalRatingForm.value.rating,
          comment: globalRatingForm.value.comment,
          is_anonymous: globalRatingForm.value.is_anonymous
        });
        if (response.data && response.data.success) {
          globalRatingForm.value.id = response.data.rating?.id || globalRatingForm.value.id || 1;
          Swal.fire({
            title: 'Thank you! ⭐',
            text: response.data.message || 'Your review has been submitted successfully.',
            icon: 'success',
            timer: 3000,
            showConfirmButton: false,
            toast: true,
            position: 'top-end'
          });
        }
      } catch (err) {
        console.error('Error saving global rating:', err);
        Swal.fire({
          title: 'Error!',
          text: err.response?.data?.message || 'Failed to submit your review.',
          icon: 'error'
        });
      } finally {
        isSubmittingGlobalRating.value = false;
      }
    };

    // Navigation Tabs computed list
    const tabs = computed(() => {
      const list = [
        { id: 'profile', label: 'Profile & Security', shortLabel: 'Profile & Security', icon: '👤' },
      ];
      if (`${authStore.user?.is_active}` === '1' && !dataStore.shouldHideWallet) {
        list.push({ id: 'wallet', label: 'Wallets & Currencies', shortLabel: 'Wallets', icon: '💳' });
      }
      list.push({ id: 'rating', label: 'My Review', shortLabel: 'My Review', icon: '⭐', badge: globalRatingForm.value.id ? 'Reviewed' : null });
      list.push({ id: 'notifications', label: 'Notifications', shortLabel: 'Notifications', icon: '🔔' });
      return list;
    });

    // Watch for user changes
    watch(() => authStore.user, (newUser) => {
      if (newUser) {
        loadUserData();
      }
    }, { immediate: true });

    // Sync tab with URL query
    watch(() => route.query.tab, (newTab) => {
      if (newTab && ['profile', 'wallet', 'rating', 'notifications'].includes(newTab)) {
        activeTab.value = newTab;
      }
    });

    watch(activeTab, (newTab) => {
      if (route.query.tab !== newTab) {
        router.replace({ query: { ...route.query, tab: newTab } });
      }
    });

    onMounted(() => {
      checkMobile();
      loadUserData();
      pushSub.checkSubscription();
      fetchGlobalRatingStatus();
    });

    return {
      activeTab,
      tabs,
      profileForm,
      passwordForm,
      selectedWallet,
      isUpdatingWallet,
      walletBalances,
      isUpdatingProfile,
      isUpdatingPassword,
      errors,
      authStore,
      dataStore,
      userInitials,
      hasProfileChanges,
      hasPasswordChanges,
      formatCurrency,
      getActiveWalletName,
      getActiveWalletBalance,
      getActiveWalletCurrency,
      selectWallet,
      updateActiveWallet,
      updateProfile,
      updatePassword,
      notificationsEmail,
      notificationsRealtime,
      isUpdatingNotif,
      toggleEmailNotifications,
      toggleRealtimeNotifications,
      browserNotif,
      pushSub,
      togglePushSub,
      isMobile,
      showNotifHelp,
      requestNotifPermission,
      canRateGlobal,
      isLoadingGlobalRating,
      isSubmittingGlobalRating,
      hoverStar,
      starLabels,
      globalRatingForm,
      submitGlobalRating,
      ratingTagSuggestions,
      addTagToComment
    };
  }
}
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.scrollbar-none::-webkit-scrollbar {
  display: none;
}
.scrollbar-none {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
